import _ from 'lodash';
import React, { useState, useEffect, useRef } from 'react';
import { Popover, Box, TextField, Button } from '@mui/material';
import { useCurrentEditor } from '@tiptap/react';
import ReplacementDropdown from '../../EmailTemplatesSection/ReplacementDropdown';

interface IHyperlinkPopoverProps {
  hyperlinkElement: HTMLElement | null;
  onPopoverClose: () => void;
}

export default function HyperlinkPopover({ hyperlinkElement, onPopoverClose }: IHyperlinkPopoverProps) {
  const { editor } = useCurrentEditor();

  const urlInputRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [selectionRange, setSelectionRange] = useState<{ from: number; to: number } | null>(null);

  useEffect(() => {
    if (editor && hyperlinkElement) {
      const { from, to } = editor.state.selection;
      setSelectionRange({ from, to });
      setText(editor.state.doc.textBetween(from, to));
      setUrl(editor.getAttributes('link').href || '');
    } else {
      setSelectionRange(null);
    }
  }, [editor, hyperlinkElement]);

  if (!editor) return null;

  const handleApply = () => {
    if (!selectionRange) return;

    if (_.isEmpty(url)) {
      editor.chain().focus().setTextSelection(selectionRange).unsetLink().run();
      onPopoverClose();
      return;
    }

    const displayText = _.isEmpty(text) ? url : text;
    const { from } = selectionRange;
    const to = from + displayText.length;

    editor
      .chain()
      .focus()
      .setTextSelection(selectionRange)
      .insertContent(displayText)
      .setTextSelection({ from, to })
      .setLink({ href: url })
      .setTextSelection(to)
      .run();

    onPopoverClose();
  };

  const handleUrlInsert = (variable: string) => {
    const urlInput = urlInputRef.current;
    if (!urlInput) return;

    const start = urlInput.selectionStart || 0;
    const end = urlInput.selectionEnd || 0;
    const newUrl = url.substring(0, start) + variable + url.substring(end);
    setUrl(newUrl);

    setTimeout(() => {
      urlInput.focus();
      const newCursorPosition = start + variable.length;
      urlInput.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  return (
    <Popover
      open={Boolean(hyperlinkElement)}
      anchorEl={hyperlinkElement}
      onClose={onPopoverClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, width: { lg: '400px', md: '100%' } }}>
        <TextField
          label="Display Text"
          size="small"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <TextField
          label="URL"
          size="small"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          inputRef={urlInputRef}
        />
        <ReplacementDropdown onInsert={handleUrlInsert} />
        <Button variant="contained" onClick={handleApply}>
          Apply
        </Button>
      </Box>
    </Popover>
  );
}
